import { Link } from 'react-router-dom';
import { getProjectsBySubSection } from '../../../mocks/projects';
import { useScrollReveal, revealClass } from '../../../hooks/useScrollReveal';

export default function ResearchSection() {
  const curatorialProjects = getProjectsBySubSection('curatorial');
  const digitalProjects = getProjectsBySubSection('digital');

  // Only curatorial and digital projects — film projects are on the Research Films page
  const allProjects = [...curatorialProjects, ...digitalProjects];

  const { ref: headingRef, isVisible: headingVisible } = useScrollReveal<HTMLDivElement>();
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal<HTMLDivElement>({ threshold: 0.05 });

  return (
    <section id="research" className="bg-cream py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div ref={headingRef} className={`mb-12 ${revealClass(headingVisible)}`}>
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-4">SELECTED WORKS</p>
          <h2 className="font-serif text-4xl lg:text-5xl font-bold text-gray-900">
            Curatorial Projects and Exhibitions
          </h2>
        </div>

        {/* Grid */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {allProjects.map((project, i) => (
            <div
              key={project.id}
              className={`bg-white rounded-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group ${revealClass(gridVisible, Math.min(i * 100, 400))}`}
            >
              <div className="w-full aspect-[4/3] overflow-hidden bg-stone-100">
                <Link to={`/project/${project.slug}`} className="block w-full h-full">
                  <img
                    src={project.image}
                    alt={`${project.title} — curated by Oludamola Adebowale`}
                    title={project.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </Link>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs uppercase tracking-wider text-accent font-semibold">
                    {project.category}
                  </p>
                  {project.year && (
                    <span className="text-xs text-gray-400">{project.year}</span>
                  )}
                </div>
                <h3 className="font-serif text-xl font-semibold text-gray-900 mb-3 leading-tight">
                  <Link
                    to={`/project/${project.slug}`}
                    className="hover:text-accent transition-colors cursor-pointer"
                  >
                    {project.title}
                  </Link>
                </h3>
                <p className="font-sans text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3">
                  {project.description}
                </p>
                <Link
                  to={`/project/${project.slug}`}
                  className="inline-flex items-center gap-1 font-sans text-xs uppercase tracking-wider text-accent hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Read More <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
